import React, { createContext, useContext, useState, useEffect } from 'react';
import { Parfum } from '../types/parfum';

export interface CartItem {
    parfum: Parfum;
    quantity: number;
}

interface CartContextType {
    cartItems: CartItem[];
    addToCart: (parfum: Parfum, quantity: number) => void;
    removeFromCart: (parfumId: string) => void;
    updateQuantity: (parfumId: string, quantity: number) => void;
    clearCart: () => void;
    getTotalPrice: () => number;
    getTotalItems: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cartItems, setCartItems] = useState<CartItem[]>(() => {
        const savedCart = localStorage.getItem('aurya_cart');
        return savedCart ? JSON.parse(savedCart) : [];
    });

    // Sauvegarder le panier dans localStorage
    useEffect(() => {
        localStorage.setItem('aurya_cart', JSON.stringify(cartItems));
    }, [cartItems]);

    const addToCart = (parfum: Parfum, quantity: number) => {
        setCartItems((prevItems) => {
            const existingItem = prevItems.find((item) => item.parfum.id === parfum.id);

            if (existingItem) {
                return prevItems.map((item) =>
                    item.parfum.id === parfum.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }

            return [...prevItems, { parfum, quantity }];
        });
    };

    const removeFromCart = (parfumId: string) => {
        setCartItems((prevItems) => prevItems.filter((item) => item.parfum.id !== parfumId));
    };

    const updateQuantity = (parfumId: string, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(parfumId);
            return;
        }

        setCartItems((prevItems) =>
            prevItems.map((item) =>
                item.parfum.id === parfumId ? { ...item, quantity } : item
            )
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    const getTotalPrice = () => {
        return cartItems.reduce((total, item) => total + (item.parfum.price || 0) * item.quantity, 0);
    };

    const getTotalItems = () => {
        return cartItems.reduce((total, item) => total + item.quantity, 0);
    };

    return (
        <CartContext.Provider
            value={{
                cartItems,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                getTotalPrice,
                getTotalItems,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
